import React, { useState, useEffect } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import PaginationList from '../../../Shared/Components/PaginationList'
import Row from 'react-bootstrap/Row'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { canViewSiaModule } from '../../../utils/siaPermissions'

const LAYOUT_OPTIONS = [
    { value: 'lesson_list', label: 'Lista lateral con datos del documento' },
    { value: 'sidebar_videos', label: 'Lista lateral + video o PDF' },
    { value: 'media_catalog', label: 'Lista lateral (mismo formato)' },
    { value: 'video_grid', label: 'Rejilla de videos' },
    { value: 'product_tabs', label: 'Pestañas (producto / llenado / …)' },
    { value: 'section_hub', label: 'Tarjetas de subcategorías' },
]

const TAB_LABELS = {
    producto: 'Producto',
    llenado: 'Llenado',
    condiciones: 'Condiciones',
    recursos: 'Recursos',
    videos_adicionales: 'Videos adicionales',
}

const ItemsIndex = ({ section, items, filters }) => {
    const { flash } = usePage().props
    const data = items.data
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [deleteSection, setDeleteSection] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const hasProcessing = data.some((row) => row.video_status === 'processing')
    const children = section.children || []
    const isHub = section.layout_type === 'section_hub'

    const subForm = useForm({
        title: '',
        order: children.length + 1,
        layout_type: 'lesson_list',
    })

    useEffect(() => {
        if (!hasProcessing) return undefined
        const id = window.setInterval(() => {
            Inertia.reload({ only: ['items'], preserveScroll: true, preserveState: true })
        }, 15000)
        return () => window.clearInterval(id)
    }, [hasProcessing])

    const filterForm = useForm({
        search: filters.search || '',
    })

    const submitFilters = (e) => {
        e.preventDefault()
        filterForm.get(route('sia.manage.section.items', section.slug), { preserveState: true })
    }

    const clearFilters = () => {
        filterForm.setData({ search: '' })
        filterForm.get(route('sia.manage.section.items', section.slug))
    }

    const paginationFilters = {
        search: filters.search || undefined,
    }

    const listPath = `/admin/sia/manage/sections/${section.slug}/items`

    const rowKindLabel = (row) => {
        if (row.type === 'document') {
            const name = String(row.file_path || '').split('/').pop() || ''
            const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
            return ext === 'xls' || ext === 'xlsx' ? 'Excel' : 'PDF'
        }
        if (row.video_status === 'processing') return 'MP4 · Procesando…'
        if (row.video_status === 'failed') return 'MP4 · Error'
        if (row.video_status === 'ready') return 'MP4'
        return 'YouTube'
    }

    const tabDisplay = (row) => (row.tab ? TAB_LABELS[row.tab] || row.tab : '—')

    const confirmDelete = () => {
        if (!deleteTarget) return
        setDeleting(true)
        Inertia.delete(route('sia.manage.items.destroy', deleteTarget.id), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setDeleteTarget(null)
            },
        })
    }

    const submitSubsection = (e) => {
        e.preventDefault()
        subForm.post(route('sia.manage.subsections.store', section.slug), {
            preserveScroll: true,
            onSuccess: () => subForm.setData('title', ''),
        })
    }

    const confirmDeleteSection = () => {
        if (!deleteSection) return
        setDeleting(true)
        Inertia.delete(route('sia.manage.sections.destroy', deleteSection.slug), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setDeleteSection(null)
            },
        })
    }

    return (
        <Row>
            {flash.success && <AlertDismissible message={flash.success} type="success" />}
            {flash.error && <AlertDismissible message={flash.error} type="danger" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath={listPath} />
            </span>

            <div className="card card-flush h-md-100">
                <div className="card-header pt-7">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-gray-800">Listado de {section.title}</span>
                        {section.parent && (
                            <span className="text-muted fs-7 mt-1">
                                Padre:{' '}
                                <Link href={route('sia.manage.section.items', section.parent.slug)}>
                                    {section.parent.title}
                                </Link>
                            </span>
                        )}
                    </h3>
                    <div className="card-toolbar d-flex flex-wrap gap-2">
                        <Link href={route('sia.manage.sections.edit', section.slug)} className="btn btn-sm btn-light">
                            Editar sección
                        </Link>
                        <Link href={route('sia.manage.items.create', section.slug)} className="btn btn-sm btn-primary">
                            Agregar archivo
                        </Link>
                        {children.length === 0 && data.length === 0 && (
                            <button type="button" className="btn btn-sm btn-light-danger" onClick={() => setDeleteSection(section)}>
                                Eliminar
                            </button>
                        )}
                    </div>
                </div>

                <div className="card-body pt-6">
                    {isHub && (
                    <div className="mb-10">
                        <h4 className="fs-6 fw-bold text-gray-800 mb-3">Subcategorías</h4>
                        <Form onSubmit={submitSubsection} className="d-flex flex-wrap align-items-end gap-3 mb-4">
                            <div style={{ minWidth: 220, flex: '1 1 240px' }}>
                                <label className="form-label fs-7 fw-bold text-gray-600 mb-2">Título</label>
                                <Form.Control
                                    value={subForm.data.title}
                                    onChange={(e) => subForm.setData('title', e.target.value)}
                                    required
                                />
                                {subForm.errors.title && <div className="text-danger small">{subForm.errors.title}</div>}
                            </div>
                            <div style={{ width: 100 }}>
                                <label className="form-label fs-7 fw-bold text-gray-600 mb-2">Orden</label>
                                <Form.Control
                                    type="number"
                                    value={subForm.data.order}
                                    onChange={(e) => subForm.setData('order', parseInt(e.target.value, 10) || 0)}
                                />
                            </div>
                            <div style={{ minWidth: 220 }}>
                                <label className="form-label fs-7 fw-bold text-gray-600 mb-2">Plantilla</label>
                                <Form.Select
                                    value={subForm.data.layout_type}
                                    onChange={(e) => subForm.setData('layout_type', e.target.value)}
                                >
                                    {LAYOUT_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            <Button type="submit" size="sm" variant="primary" disabled={subForm.processing}>
                                Agregar subcategoría
                            </Button>
                        </Form>
                        <div className="table-responsive">
                            <table className="table table-row-dashed align-middle gs-0 gy-3 my-0">
                                <thead>
                                    <tr className="fs-7 fw-bold text-gray-400 border-bottom-0">
                                        <th className="p-0 pb-3 text-start">Título</th>
                                        <th className="p-0 pb-3 text-start">Orden</th>
                                        <th className="p-0 pb-3 text-start">Contenidos</th>
                                        <th className="p-0 pb-3 text-end">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {children.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="text-muted py-4">
                                                No hay subcategorías.
                                            </td>
                                        </tr>
                                    )}
                                    {children.map((child) => (
                                        <tr key={child.id}>
                                            <td className="fw-semibold text-gray-800">{child.title}</td>
                                            <td>{child.order}</td>
                                            <td>{child.items_count ?? 0}</td>
                                            <td>
                                                <div className="d-flex justify-content-end flex-wrap gap-3">
                                                    <Link
                                                        href={route('sia.manage.section.items', child.slug)}
                                                        className="btn btn-sm btn-primary"
                                                    >
                                                        Entrar
                                                    </Link>
                                                    <Link
                                                        href={route('sia.manage.sections.edit', child.slug)}
                                                        className="btn btn-sm btn-light-primary"
                                                    >
                                                        Editar
                                                    </Link>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-light-danger"
                                                        onClick={() => setDeleteSection(child)}
                                                    >
                                                        Eliminar
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    )}

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                        <h4 className="fs-6 fw-bold text-gray-800 mb-0">Archivos de esta sección</h4>
                        <div className="d-flex gap-2">
                            {canViewSiaModule('examenes', true) && (
                                <Link href={route('sia.manage.exams.edit', section.slug)} className="btn btn-sm btn-light-primary">
                                    Examen
                                </Link>
                            )}
                            <Link href={route('sia.manage.items.create', section.slug)} className="btn btn-sm btn-primary">
                                Agregar archivo
                            </Link>
                        </div>
                    </div>

                    <Form onSubmit={submitFilters} className="d-flex flex-wrap align-items-end gap-3 mb-8">
                        <div className="flex-grow-1" style={{ minWidth: '220px', maxWidth: '420px' }}>
                            <label className="form-label fs-7 fw-bold text-gray-600 mb-2">Buscar</label>
                            <Form.Control
                                type="search"
                                className="form-control form-control-sm form-control-solid"
                                placeholder="Título o descripción…"
                                value={filterForm.data.search}
                                onChange={(e) => filterForm.setData('search', e.target.value)}
                            />
                        </div>
                        <Button type="submit" size="sm" variant="primary" disabled={filterForm.processing}>
                            Buscar
                        </Button>
                        <Button type="button" size="sm" variant="light" onClick={clearFilters}>
                            Limpiar
                        </Button>
                    </Form>

                    <div className="table-responsive">
                        <table className="table table-row-dashed align-middle gs-0 gy-3 my-0">
                            <thead>
                                <tr className="fs-7 fw-bold text-gray-400 border-bottom-0">
                                    <th className="p-0 pb-3 min-w-50px text-start">ID</th>
                                    <th className="p-0 pb-3 min-w-125px text-start">Pestaña</th>
                                    <th className="p-0 pb-3 min-w-200px text-start">Nombre</th>
                                    <th className="p-0 pb-3 min-w-100px text-start">Tipo</th>
                                    <th className="p-0 pb-3 min-w-75px text-start">Orden</th>
                                    <th className="p-0 pb-3 min-w-125px text-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 && (
                                    <tr>
                                        <td className="px-6 py-3 text-center text-gray-800 fw-bold mb-1 fs-6" colSpan={6}>
                                            No hay datos.
                                        </td>
                                    </tr>
                                )}
                                {data.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="d-flex justify-content-start flex-column">
                                                    <p className="text-gray-600 mb-1 fs-7">{row.id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="d-flex justify-content-start flex-column">
                                                    <p className="text-gray-800 mb-1 fs-6">{tabDisplay(row)}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <p className="text-gray-800 fw-bold mb-1 fs-6">{row.title}</p>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="d-flex justify-content-start flex-column">
                                                    <p
                                                        className={`text-gray-800 mb-1 fs-6 ${
                                                            row.type === 'document' ? 'fw-bold' : ''
                                                        }`}
                                                    >
                                                        {row.type === 'document' ? (
                                                            rowKindLabel(row)
                                                        ) : (
                                                            <span className="text-gray-400">{rowKindLabel(row)}</span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="d-flex justify-content-start flex-column">
                                                    <p className="text-gray-800 fw-bold mb-1 fs-6">{row.order}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-end flex-wrap gap-3">
                                                {row.video_status === 'processing' ? (
                                                    <span className="text-muted fs-7">Disponible al terminar</span>
                                                ) : (
                                                    <>
                                                        <Link
                                                            href={route('sia.manage.items.edit', row.id)}
                                                            className="btn btn-sm btn-light-primary"
                                                        >
                                                            Editar
                                                        </Link>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-light-danger"
                                                            onClick={() => setDeleteTarget(row)}
                                                        >
                                                            Eliminar
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-center py-6">
                        <PaginationList links={items.links} filters={paginationFilters} />
                    </div>
                </div>
            </div>

            <Modal show={deleteSection !== null} onHide={() => !deleting && setDeleteSection(null)} centered>
                <Modal.Header closeButton={!deleting}>
                    <Modal.Title>Eliminar subsección</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Eliminar la subsección «{deleteSection?.title}»? Solo se puede si no tiene contenido.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" disabled={deleting} onClick={() => setDeleteSection(null)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" disabled={deleting} onClick={confirmDeleteSection}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={deleteTarget !== null} onHide={() => !deleting && setDeleteTarget(null)} centered>
                <Modal.Header closeButton={!deleting}>
                    <Modal.Title>Confirmar eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Eliminar el contenido «{deleteTarget?.title}»? Esta acción no se puede deshacer.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" disabled={deleting} onClick={() => setDeleteTarget(null)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Row>
    )
}

ItemsIndex.layout = (page) => <Layout title={`SIA · ${page.props.section?.title || 'Contenido'}`}>{page}</Layout>

export default ItemsIndex
